<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import { ElMessage } from 'element-plus';
import axios from 'axios';
import { 
  validateNumericInput,
  calculateBinocularPupilDistance,
  setupEnterKeyNavigationWithObserver,
  getUserPurchasingUnits,
  fillDefaultZeroFields,
  sanitizeFormData,
  convertVisionFieldsInForm
} from '@/utils/opticalDataUtil';
import {
  isBetaHistoryModel,
  normalizeBetaHistoryDuration,
  parseBetaHistoryDuration
} from '@/utils/betaHistoryDuration';

// 当前步骤状态
const currentStep = ref(1); // 1:填写订单, 2:确认信息, 3:提交成功

// 读取本地用户角色，判断是否为管理员
const isAdmin = computed(() => {
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  return userInfo.role === 'ADMIN';
});
const isSpecialUser = computed(() => localStorage.getItem('userId') === '1');
const lensModelOptions = ref([]);
const modelInfoMap = ref({});
const showOriginalFrameOption = computed(() => isSpecialUser.value || formData.lensModel === '超主点β4');
const isFrameNumberRequired = computed(() => formData.frameType !== '原片');
const isBetaStarLensModel = isBetaHistoryModel;
const selectedLensModelOption = computed(() => lensModelOptions.value.find(item =>
  item.value === formData.lensModel || item.label === formData.lensModel
));
const isBetaStarModel = computed(() => isBetaStarLensModel(formData.lensModel)
  || isBetaStarLensModel(selectedLensModelOption.value?.label)
  || isBetaStarLensModel(selectedLensModelOption.value?.value));
const showAtropineStopDuration = computed(() => isBetaStarModel.value && formData.atropineEyeDropsUsed === '是');
const showOrthokeratologyStopDuration = computed(() => isBetaStarModel.value && formData.orthokeratologyLensUsed === '是');
const DEFAULT_BETA_HISTORY_USED = '否';

const isSubmitting = ref(false);
const betaHistoryDialogVisible = ref(false);
const betaHistoryDialogMessage = ref('');

// Excel解析的原始型号（仅用于显示）
const excelOriginalModel = ref('');
// Excel解析的原始验配单位（仅在匹配失败时用于提示）
const excelOriginalCompany = ref('');

// 使用本地时区日期，避免 UTC 导致日期偏差
const getTodayLocalDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// 型号备注框直接显示，不再需要激活功能
// const hiddenRemarksActivated = ref(false); // 已移除激活功能

// 控制隐藏备注框是否为必填
const isHiddenRemarksRequired = ref(false);

// 表单数据
const formData = reactive({
  // 订单基本信息
  orderDate: getTodayLocalDate(),
  orderType:'散订单',
  patientName: '',
  age: '',
  optometrist: '',
  region: '',
  lensModel: '',
  company: '',
  purchasingUnits: '',

  // 超主点β型号既往用药及OK镜情况
  atropineEyeDropsUsed: DEFAULT_BETA_HISTORY_USED,
  atropineEyeDropsStopDuration: '',
  orthokeratologyLensUsed: DEFAULT_BETA_HISTORY_USED,
  orthokeratologyLensStopDuration: '',

  // 常规验光数据
  regularOdSphere: '',
  regularOdCylinder: '',
  regularOdAxis: '',
  regularOdVision: '',
  regularOsSphere: '',
  regularOsCylinder: '',
  regularOsAxis: '',
  regularOsVision: '',
  regularBinocularVision: '',
  regularBinocularFusion:'',
  regularStereoscopicVisionFunction:'',

  // 散瞳验光数据
  cycloplegicOdSphere: '',
  cycloplegicOdCylinder: '',
  cycloplegicOdAxis: '',
  cycloplegicOdVision: '',
  cycloplegicOsSphere: '',
  cycloplegicOsCylinder: '',
  cycloplegicOsAxis: '',
  cycloplegicOsVision: '',

  // 超主点验光数据
  superOdSphere: '',
  superOdCylinder: '',
  superOdAxis: '',
  superOdVision: '',
  odQuantity: '1',
  superOsSphere: '',
  superOsCylinder: '',
  superOsAxis: '',
  superOsVision: '',
  osQuantity: '1',
  superBinocularVision: '',
  superBinocularFusion:'',
  superStereoscopicVisionFunction:'',

  // 角膜曲率
  odK1: '',
  odK2: '',
  odAxialLength: '',
  odChoroidalThickness: '',
  osK1: '',
  osK2: '',
  osAxialLength: '',
  osChoroidalThickness: '',

  // 眼镜参数（镜框类型不再设置默认值，必须用户手动选择）
  odPupilHeight: '',
  odPupilDistance: '',
  osPupilHeight: '',
  osPupilDistance: '',
  binocularPupilDistance: '',
  frameNumber: '',
  frameType: '',

  // 双眼视功能
  binocularFusion: '',
  stereoscopicVisionFunction: '',

  // 邮寄信息
  addressee: '',
  phone: '',
  shippingAddress: '',
  remarks: '',
  
  // 隐藏备注（用于型号备注）
  hiddenRemarks: ''
});

// 停药/停戴时长直接选择月数和天数，提交时合并为“X个月Y天”保存。
const durationMonthOptions = Array.from({ length: 121 }, (_, value) => ({
  label: String(value),
  value
}));
const durationDayOptions = Array.from({ length: 32 }, (_, value) => ({
  label: String(value),
  value
}));

const parseDuration = (duration) => {
  return parseBetaHistoryDuration(duration);
};

const getDurationPart = (duration, part) => parseDuration(duration)?.[part] ?? '';

const updateDurationPart = (field, part, value) => {
  if (value === '' || value === null || value === undefined) {
    formData[field] = '';
    return;
  }

  const current = parseDuration(formData[field]);
  const months = part === 'months' ? Number(value) : (current?.months ?? 0);
  const days = part === 'days' ? Number(value) : (current?.days ?? 0);
  formData[field] = `${months}个月${days}天`;
};

const applyBetaHistoryDefaults = () => {
  if (!isBetaStarModel.value) return;
  if (!formData.atropineEyeDropsUsed) {
    formData.atropineEyeDropsUsed = DEFAULT_BETA_HISTORY_USED;
  }
  if (!formData.orthokeratologyLensUsed) {
    formData.orthokeratologyLensUsed = DEFAULT_BETA_HISTORY_USED;
  }
};

const atropineStopMonths = computed({
  get: () => getDurationPart(formData.atropineEyeDropsStopDuration, 'months'),
  set: value => updateDurationPart('atropineEyeDropsStopDuration', 'months', value)
});
const atropineStopDays = computed({
  get: () => getDurationPart(formData.atropineEyeDropsStopDuration, 'days'),
  set: value => updateDurationPart('atropineEyeDropsStopDuration', 'days', value)
});
const orthokeratologyStopMonths = computed({
  get: () => getDurationPart(formData.orthokeratologyLensStopDuration, 'months'),
  set: value => updateDurationPart('orthokeratologyLensStopDuration', 'months', value)
});
const orthokeratologyStopDays = computed({
  get: () => getDurationPart(formData.orthokeratologyLensStopDuration, 'days'),
  set: value => updateDurationPart('orthokeratologyLensStopDuration', 'days', value)
});

const initFrameTypeByUser = () => {
  formData.frameType = isSpecialUser.value ? '原片' : '';
};



// SECOND_EDIT: 在脚本中新增 Excel 导入相关的 ref 与方法
const excelFileInput = ref(null);
const uploadInProgress = ref(false); // 新增：用于跟踪上传状态

const triggerExcelUpload = () => {
  if (excelFileInput.value) {
    // 为了能重复选择同一文件，先清空 value
    excelFileInput.value.value = null;
    excelFileInput.value.click();
  }
};

const handleExcelUpload = async (e) => {
  const file = e.target.files[0];
  if (!file) return;
  
  // 验证文件格式
  if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
    ElMessage.error('请选择 Excel 文件 (.xlsx 或 .xls)');
    return;
  }
  
  // 验证文件大小 (10MB以下)
  const maxSizeBytes = 10 * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    ElMessage.warning('文件过大，可能会导致解析较慢或失败。建议使用小于10MB的Excel文件。');
  }

  // 清除上一份Excel导入留下的验配单位提示
  excelOriginalCompany.value = '';
  uploadInProgress.value = true; // 开始上传
  const form = new FormData();
  form.append('file', file);

  try {
    // 显示加载进度
    const loadingInstance = ElMessage({
      type: 'info',
      message: '正在导入Excel数据，大文件可能需要多一点时间...',
      duration: 0
    });
    
    const res = await axios.post('/api/v1/user/order/importexcel', form, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      timeout: 60000 // 增加超时时间到60秒
    });
    
    // 关闭加载消息
    loadingInstance.close();
    
    if (res.data.code === '200' && res.data.data) {
      const row = res.data.data;
      
      // 填充除型号和验配单位外的所有字段
      Object.keys(row).forEach(key => {
        if (key !== 'lensModel' && key !== 'company' && key in formData) {
          formData[key] = row[key] !== undefined && row[key] !== null ? String(row[key]) : '';
        }
      });
      formData.atropineEyeDropsStopDuration = normalizeBetaHistoryDuration(formData.atropineEyeDropsStopDuration);
      formData.orthokeratologyLensStopDuration = normalizeBetaHistoryDuration(formData.orthokeratologyLensStopDuration);
      
      // 处理验配单位匹配结果
      const companyMatchStatus = row.companyMatchStatus;
      if (companyMatchStatus === 'matched') {
        // 匹配成功，验配单位已在后端处理
        formData.company = row.company;
        excelOriginalCompany.value = '';
        ElMessage.success(`Excel数据已导入！验配单位已自动匹配为：${row.company}`);
      } else if (companyMatchStatus === 'failed') {
        // 匹配失败，已置空
        formData.company = '';
        excelOriginalCompany.value = row.originalCompany || '';
        ElMessage.warning(`Excel数据已导入！验配单位"${row.originalCompany}"未能匹配，请手动选择验配单位或者手动补充验配单位。`);
      } else if (companyMatchStatus === 'no_clinic') {
        // 用户没有配置验配单位
        formData.company = '';
        excelOriginalCompany.value = row.originalCompany || '';
        ElMessage.warning(`Excel数据已导入！您尚未配置验配单位，请先在验配单位管理中配置后再导入。`);
      } else if (companyMatchStatus === 'error') {
        // 匹配过程出错
        formData.company = '';
        excelOriginalCompany.value = row.originalCompany || '';
        ElMessage.warning('Excel数据已导入！验配单位匹配过程出现错误，请手动选择验配单位。');
      } else {
        // 没有验配单位信息或其他情况
        formData.company = row.company || '';
        excelOriginalCompany.value = '';
      }
      
      // 处理型号匹配结果
      const matchStatus = row.modelMatchStatus;
      if (matchStatus === 'matched') {
        // 匹配成功，型号已在后端处理
        formData.lensModel = row.lensModel;
        excelOriginalModel.value = row.originalModel;
        ElMessage.success(`Excel数据已导入！型号已自动匹配为：${row.lensModel}`);
      } else if (matchStatus === 'failed') {
        // 匹配失败，填充原始型号到备注框
        formData.lensModel = '';
        excelOriginalModel.value = row.originalModel;
        formData.hiddenRemarks = `${row.originalModel}`;
        ElMessage.warning(`Excel数据已导入！型号"${row.originalModel}"未能匹配，请手动选择型号。`);
      } else if (matchStatus === 'error') {
        // 匹配过程出错
        formData.lensModel = row.lensModel || '';
        excelOriginalModel.value = row.originalModel || '';
        ElMessage.warning('Excel数据已导入！型号匹配过程出现错误，请手动选择型号。');
      } else {
        // 没有型号信息或其他情况
        formData.lensModel = row.lensModel || '';
        excelOriginalModel.value = row.originalModel || '';
        ElMessage.success('Excel数据已导入！请手动选择订单型号');
      }

      applyBetaHistoryDefaults();
      
      // Excel 数据填充后统一执行一次转换
      runAllConversions();
    } else {
      ElMessage.error(res.data.info || 'Excel导入失败');
    }
  } catch (error) {
    console.error('Excel导入失败:', error);
    if (error.message?.includes('timeout') || error.code === 'ECONNABORTED') {
      ElMessage.error('Excel导入超时，文件可能过大。请尝试拆分文件或手动填写数据');
    } else if (error.response?.status === 413) {
      ElMessage.error('Excel文件过大，超出服务器限制');
    } else if (error.message?.includes('内存不足') || (error.response?.data && error.response.data.info?.includes('内存不足'))) {
      ElMessage.error('服务器内存不足，请尝试拆分Excel文件或简化文件内容');
    } else {
      ElMessage.error('Excel上传解析失败，请稍后重试');
    }
  } finally {
    uploadInProgress.value = false; // 结束上传
  }
};



// 用于表单验证的规则
const validationRules = [
  { field: 'patientName', message: '请输入患者姓名' },
  { field: 'lensModel', message: '请选择订单型号' },
  { field: 'company', message: '请输入验配单位' },
  { field: 'purchasingUnits', message: '请输入采购单位' },
  { field: 'age', message: '请输入年龄' },
  { field: 'regularOdSphere', message: '请输入常规验光右眼球镜' },
  { field: 'regularOdVision', message: '请输入常规验光右眼视力' },
  { field:'regularOdCylinder', message: '请输入常规验光右眼柱镜'},
  { field:'regularOdAxis', message: '请输入常规验光右眼轴位' },
  { field: 'regularOsSphere', message: '请输入常规验光左眼球镜' },
  { field: 'regularOsVision', message: '请输入常规验光左眼视力' },
  { field: 'regularOsAxis', message: '请输入常规验光左眼轴位'},
  { field:'regularOsCylinder', message: '请输入常规验光左眼柱镜'},
  { field: 'superOdSphere', message: '请输入超主点验光右眼球镜' },
  { field: 'superOdAxis', message: '请输入超主点验光右眼轴位'},
  { field:'superOdCylinder', message: '请输入超主点验光右眼柱镜'},
  { field: 'superOdVision', message: '请输入超主点验光右眼视力' },
  { field: 'superOsSphere', message: '请输入超主点验光左眼球镜' },
  { field: 'superOsAxis', message: '请输入超主点验光左眼轴位'},
  { field:'superOsCylinder', message: '请输入超主点验光左眼柱镜'},
  { field: 'superOsVision', message: '请输入超主点验光左眼视力' },
  { field: 'odK1', message: '请输入角膜曲率右眼K1' },
  { field: 'odK2', message: '请输入角膜曲率右眼K2' },
  { field: 'odAxialLength', message: '请输入角膜曲率右眼眼轴' },
  { field: 'osK1', message: '请输入角膜曲率左眼K1' },
  { field: 'osK2', message: '请输入角膜曲率左眼K2' },
  { field: 'osAxialLength', message: '请输入角膜曲率左眼眼轴' },
  { field: 'odPupilHeight', message: '请输入右眼瞳高' },
  { field: 'osPupilHeight', message: '请输入左眼瞳高' },
  { field: 'odPupilDistance', message: '请输入右眼瞳距' },
  { field: 'osPupilDistance', message: '请输入左眼瞳距' },
  { field: 'frameType', message: '请选择镜框类型'},
  { field:'addressee', message: '请输入收件人姓名'},
  { field: 'shippingAddress', message: '请输入邮寄地址' },
  { field: 'phone', message: '请输入联系电话'}
];

// 地区选项
const regionOptions = [
  {"value": "黑龙江省"},
  { "value": "吉林省" },
  { "value": "辽宁省" },
  { "value": "河北省" },
  { "value": "河南省" },
  { "value": "山东省" },
  { "value": "山西省" },
  { "value": "陕西省" },
  { "value": "甘肃省" },
  { "value": "青海省" },
  { "value": "四川省" },
  { "value": "云南省" },
  { "value": "贵州省" },
  { "value": "湖南省" },
  { "value": "湖北省" },
  { "value": "江西省" },
  { "value": "安徽省" },
  { "value": "江苏省" },
  { "value": "浙江省" },
  { "value": "福建省" },
  { "value": "广东省" },
  { "value": "海南省" },
  { "value": "台湾省" },
  { "value": "内蒙古自治区" },
  { "value": "广西壮族自治区" },
  { "value": "西藏自治区" },
  { "value": "宁夏回族自治区" },
  { "value": "新疆维吾尔自治区" },
  { "value": "北京市" },
  { "value": "天津市" },
  { "value": "上海市" },
  { "value": "重庆市" },
  { "value": "香港特别行政区" },
  { "value": "澳门特别行政区" }
];

// 地区选项：下拉选择（不允许自由输入）

// 获取型号列表（使用按用户过滤的接口，后端根据开关决定是否过滤）
const fetchLensModels = async () => {
  try {
    const response = await axios.get('/api/v1/model-record/list/filtered/regular', {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (response.data.code === '200') {
      const models = response.data.data || [];
      lensModelOptions.value = models.map(item => ({
        value: item.model,
        label: item.model
      }));
      // 构建型号信息映射
      modelInfoMap.value = {};
      models.forEach(item => {
        modelInfoMap.value[item.model] = {
          enabled: item.enabled === true
        };
      });
    } else {
      console.error('获取型号列表失败:', response.data.info);
      ElMessage.warning('获取型号列表失败，请刷新页面重试');
    }
  } catch (error) {
    console.error('获取型号列表失败:', error);
    ElMessage.error('获取型号列表失败，请检查网络连接');
  }
};

// 验配单位选项（从后端获取）
const optometryClinicOptions = ref([]);

// 获取验配单位列表
const fetchOptometryClinics = async () => {
  try {
    const response = await axios.get('/api/v1/inspection-management/all', {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (response.data.code === '200') {
      const clinics = response.data.data || [];
      // 提取不重复的验配单位名称
      const uniqueClinics = [...new Set(clinics.map(item => item.optometryClinic).filter(Boolean))];
      optometryClinicOptions.value = uniqueClinics.map(clinic => ({
        value: clinic,
        label: clinic
      }));
    } else {
      console.error('获取验配单位列表失败:', response.data.info);
      // 不显示错误消息，允许手动输入
    }
  } catch (error) {
    console.error('获取验配单位列表失败:', error);
    // 不显示错误消息，允许手动输入
  }
};

// 下单提醒：年龄+型号不匹配时提示（仅提醒，不限制提交）
const AGE_LE18_WARN_MODELS = ['超主点β4', '超主点β1']; // ≤18 岁选中则提醒
const AGE_GT18_WARN_MODELS = ['超主点β2-', '超主点β2- H眼加点阵', '超主点β2', '超主点β2 H眼加点阵']; // >18 岁选中则提醒
const showModelAccuracyReminder = computed(() => {
  const ageStr = String(formData.age || '').trim();
  if (!ageStr || !formData.lensModel) return false;
  const ageNum = parseInt(ageStr, 10);
  if (Number.isNaN(ageNum)) return false;
  if (ageNum <= 18 && AGE_LE18_WARN_MODELS.includes(formData.lensModel)) return true;
  if (ageNum > 18 && AGE_GT18_WARN_MODELS.includes(formData.lensModel)) return true;
  return false;
});

const showCompanyMismatchWarning = computed(() => {
  return !!excelOriginalCompany.value && !formData.company;
});

// 型号备注框的placeholder（动态计算）
const hiddenRemarksPlaceholder = computed(() => {
  if (!formData.lensModel) {
    return '如有特殊情况请备注';
  }
  const modelInfo = modelInfoMap.value[formData.lensModel];
  if (modelInfo && modelInfo.enabled) {
    return `${formData.lensModel}请填写装配方向:正向(寄原片)/反向(来框安装)`;
  }
  return '如有特殊情况请备注';
});

// 检查备注框是否包含装配方向相关关键词
const checkRemarksForKeywords = (remarks) => {
  if (!remarks || typeof remarks !== 'string') {
    return false;
  }
  const keywords = ['正向', '寄原片', '反向', '来框安装'];
  return keywords.some(keyword => remarks.includes(keyword));
};

const isBetaHistoryValidationError = (message) => {
  if (typeof message !== 'string') return false;
  return message.includes('阿托品滴眼液')
    || message.includes('角膜塑形镜')
    || message.includes('OK镜');
};

const openBetaHistoryDialog = (message) => {
  betaHistoryDialogMessage.value = message || '请补充超主点β既往情况';
  betaHistoryDialogVisible.value = true;
};

const showBetaHistoryValidationError = (message) => {
  ElMessage.error(message);
  openBetaHistoryDialog(message);
  return false;
};

// 监听型号选择变化
watch(() => formData.lensModel, (newModel, oldModel) => {
  if (!isBetaStarModel.value) {
    formData.atropineEyeDropsUsed = '';
    formData.atropineEyeDropsStopDuration = '';
    formData.orthokeratologyLensUsed = '';
    formData.orthokeratologyLensStopDuration = '';
  } else {
    applyBetaHistoryDefaults();
  }

  if (newModel && newModel !== oldModel) {
    if (!showOriginalFrameOption.value && formData.frameType === '原片') {
      formData.frameType = '';
    }

    const modelInfo = modelInfoMap.value[newModel];
    if (modelInfo && modelInfo.enabled) {
      // 检查备注框是否已包含关键词
      const hasKeywords = checkRemarksForKeywords(formData.remarks);
      
      if (hasKeywords) {
        // 如果备注框已有相关内容，按原本逻辑处理
        isHiddenRemarksRequired.value = false;
        const message = `${newModel}请填写装配方向:正向(寄原片)/反向(来框安装)`;
        ElMessage({
          message: message,
          type: 'info',
          duration: 5000,
          showClose: true
        });
      } else {
        // 如果没有，设置隐藏备注框为必填
        isHiddenRemarksRequired.value = true;
        const message = `${newModel}请务必填写装配方向:正向(寄原片)/反向(来框安装)`;
        ElMessage({
          message: message,
          type: 'warning',
          duration: 5000,
          showClose: true
        });
      }
    } else {
      // 型号未启用，取消必填要求
      isHiddenRemarksRequired.value = false;
    }
  }
});

// 型号列表异步加载后，使用下拉项展示文本再次确认β型号状态。
watch(lensModelOptions, () => {
  applyBetaHistoryDefaults();
});

watch(() => formData.atropineEyeDropsUsed, (value) => {
  if (value !== '是') {
    formData.atropineEyeDropsStopDuration = '';
  }
});

watch(() => formData.orthokeratologyLensUsed, (value) => {
  if (value !== '是') {
    formData.orthokeratologyLensStopDuration = '';
  }
});

// 监听备注框变化，实时检查是否需要必填
watch(() => formData.remarks, (newRemarks) => {
  if (formData.lensModel) {
    const modelInfo = modelInfoMap.value[formData.lensModel];
    if (modelInfo && modelInfo.enabled) {
      const hasKeywords = checkRemarksForKeywords(newRemarks);
      // 如果备注框已包含关键词，取消必填要求
      if (hasKeywords) {
        isHiddenRemarksRequired.value = false;
      } else {
        // 否则保持必填要求
        isHiddenRemarksRequired.value = true;
      }
    }
  }
});

// // 镜片型号搜索功能
// const querySearchLensModel = (queryString, cb) => {
//   const results = queryString
//     ? lensModelOptions.value.filter(item => item.label.includes(queryString))
//     : lensModelOptions.value;
//   cb(results);
// };


// 处理超主点验光柱镜转换
// const handleSuperCylinderTransform = (eye) => {
//   handleCylinderTransform(eye, formData, needAxisAdjustment, 'super');
// };

// 表单验证
const validateForm = () => {
  // 提交前：对数视力 → 小数视力（方案B，点击提交时触发）
  convertVisionFieldsInForm(formData);
  // BEFORE_VALIDATION_FILL_ZERO_START
  // 先填充柱镜、轴位默认值，以免与必填校验冲突
  fillDefaultZeroFields(formData);
  // BEFORE_VALIDATION_FILL_ZERO_END
  for (const rule of validationRules) {
    if (!formData[rule.field]) {
      ElMessage.error(rule.message);
      return false;
    }
  }

  if (isFrameNumberRequired.value && !formData.frameNumber) {
    ElMessage.error('请输入镜框编号');
    return false;
  }
  
  // 检查隐藏备注框是否必填且为空
  if (isHiddenRemarksRequired.value && (!formData.hiddenRemarks || !formData.hiddenRemarks.trim())) {
    ElMessage.error('请填写型号备注（装配方向：正向(寄原片)/反向(来框安装)）');
    return false;
  }

  if (isBetaStarModel.value) {
    if (!formData.atropineEyeDropsUsed) {
      return showBetaHistoryValidationError('请选择是否有使用阿托品滴眼液');
    }
    if (showAtropineStopDuration.value && !formData.atropineEyeDropsStopDuration.trim()) {
      return showBetaHistoryValidationError('请选择阿托品滴眼液停药时长');
    }
    if (!formData.orthokeratologyLensUsed) {
      return showBetaHistoryValidationError('请选择是否配戴过角膜塑形镜（OK镜）');
    }
    if (showOrthokeratologyStopDuration.value && !formData.orthokeratologyLensStopDuration.trim()) {
      return showBetaHistoryValidationError('请选择角膜塑形镜（OK镜）停戴时长');
    }
  }
  
  // 新增：校验数字字段是否包含非法字符，尤其防止 Excel 导入带入非数字内容
  const numericFinalizeRegex = /^[-+]?\d+(?:\.\d+)?$/; // 完整数字格式
  const isNumericFieldKey = (key) => {
    return /(Sphere|Cylinder|Axis|K1|K2|AxialLength|ChoroidalThickness|PupilHeight|PupilDistance|binocularPupilDistance|Quantity)$/i.test(key);
  };
  // 根据属性名生成中文字段名
  const getFieldLabel = (key) => {
    if (key === 'binocularPupilDistance') return '双眼瞳距';
    if (key === 'odQuantity') return '右眼数量';
    if (key === 'osQuantity') return '左眼数量';

    let prefixCn = '';
    let eyeCn = '';
    let suffixCn = '';

    // 前缀：regular / super
    if (key.startsWith('regular')) {
      prefixCn = '常规验光';
      key = key.replace(/^regular/, '');
    } else if (key.startsWith('super')) {
      prefixCn = '超主点验光';
      key = key.replace(/^super/, '');
    }

    // 眼别：Od / Os / od / os
    if (/Od/.test(key)) {
      eyeCn = '右眼';
      key = key.replace(/Od/, '');
    } else if (/Os/.test(key)) {
      eyeCn = '左眼';
      key = key.replace(/Os/, '');
    } else if (/^od/.test(key)) {
      eyeCn = '右眼';
      key = key.replace(/^od/, '');
    } else if (/^os/.test(key)) {
      eyeCn = '左眼';
      key = key.replace(/^os/, '');
    }

    const suffixMap = {
      Sphere: '球镜',
      Cylinder: '柱镜',
      Axis: '轴位',
      Vision: '视力',
      K1: 'K1',
      K2: 'K2',
      AxialLength: '眼轴',
      ChoroidalThickness: '脉络膜厚度',
      PupilHeight: '瞳高',
      PupilDistance: '瞳距',
      Quantity: '数量'
    };

    const suffixKey = Object.keys(suffixMap).find(suf => key.endsWith(suf));
    if (suffixKey) {
      suffixCn = suffixMap[suffixKey];
    }

    const label = `${prefixCn}${eyeCn}${suffixCn}`;
    return label || key;
  };

  for (const key in formData) {
    if (isNumericFieldKey(key) && formData[key] !== '' && formData[key] !== '/' && !numericFinalizeRegex.test(formData[key])) {
      const label = getFieldLabel(key);
      ElMessage.error(`字段【${label}】含有非法字符，请输入有效数字`);
      return false;
    }
  }
  // 数字非法字符校验结束
  return true;
};

// 提交订单预览
const handlePreviewSubmit = async () => {
  // 跨天停留页面时，提交前刷新为当天日期
  formData.orderDate = getTodayLocalDate();
  if (!validateForm()) {
    return;
  }
  
  // 更改步骤状态为确认信息
  currentStep.value = 2;
  
  // 滚动到页面顶部
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// 提交订单
const handleSubmit = async () => {
  if (isSubmitting.value) {
    ElMessage.warning('订单正在提交，请勿重复点击');
    return;
  }
  try {
    isSubmitting.value = true;
    // 最终提交前再次刷新订单日期，覆盖跨天停留场景
    formData.orderDate = getTodayLocalDate();
    
    // 1. 首先清理所有字符串字段中的控制字符
    let processedFormData = sanitizeFormData({ ...formData });
    
    // 2. 整合型号备注到主备注中（如果有内容时才合并）
    if (processedFormData.hiddenRemarks && processedFormData.hiddenRemarks.trim()) {
      const modelRemark = `型号备注：${processedFormData.hiddenRemarks.trim()}`;
      if (processedFormData.remarks && processedFormData.remarks.trim()) {
        processedFormData.remarks = `${processedFormData.remarks.trim()}\n${modelRemark}`;
      } else {
        processedFormData.remarks = modelRemark;
      }
    }
    
    // 3. 删除隐藏备注字段，不提交到后端
    delete processedFormData.hiddenRemarks;
    
    // 4. 处理空字符串为 null；数值字段中的 '/' 也转为 null
    const numericFieldPattern = /(Sphere|Cylinder|Axis|K1|K2|AxialLength|ChoroidalThickness|PupilHeight|PupilDistance|binocularPupilDistance|Quantity)$/i;
    Object.keys(processedFormData).forEach(key => {
      const val = processedFormData[key];
      if (val === '') {
        processedFormData[key] = null;
      }
      if (val === '/' && numericFieldPattern.test(key)) {
        processedFormData[key] = null;
      }
    });
    
    const orderData = {
      ...processedFormData,
      userId: localStorage.getItem('userId'),
      status: 'Noexported' // 默认为未导出状态
    };
    console.log('提交订单数据:', orderData);
    const response = await axios.post('/api/v1/user/order', orderData, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (response.data.code === '200') {
      ElMessage.success('超主点订单提交成功');
      // 更改步骤状态为提交成功
      currentStep.value = 3;
      // 清除Excel原始型号提示
      excelOriginalModel.value = '';
      // 清除Excel原始验配单位提示
      excelOriginalCompany.value = '';
      // 提交成功后刷新历史地址
      fetchAddressOptions();
      // 滚动到页面顶部
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const message = response.data.info || '提交失败';
      if (isBetaHistoryValidationError(message)) {
        showBetaHistoryValidationError(message);
      } else {
        ElMessage.error(message);
      }
    }
  } catch (error) {
    console.error('提交订单失败:', error);
    const message = error.response?.data?.info;
    if (isBetaHistoryValidationError(message)) {
      showBetaHistoryValidationError(message);
    } else {
      ElMessage.error('提交失败，请检查网络连接');
    }
  } finally {
    isSubmitting.value = false;
  }
};

// 返回编辑表单
const backToEdit = () => {
  currentStep.value = 1;
};

// 重新开始填写新订单
const startNewOrder = () => {
  resetForm();
  currentStep.value = 1;
};

// 重置表单
const resetForm = () => {
  const keepKeys = ['orderType', 'purchasingUnits', 'odQuantity', 'osQuantity'];
  Object.keys(formData).forEach(key => {
    if (!keepKeys.includes(key)) {
      formData[key] = '';
    }
  });
  formData.orderDate = getTodayLocalDate();
  formData.atropineEyeDropsUsed = DEFAULT_BETA_HISTORY_USED;
  formData.orthokeratologyLensUsed = DEFAULT_BETA_HISTORY_USED;
  // 清除Excel原始型号提示
  excelOriginalModel.value = '';
  // 清除Excel原始验配单位提示
  excelOriginalCompany.value = '';
  initFrameTypeByUser();
};

// 生命周期钩子 - 页面挂载时执行
onMounted(() => {
  // 页面加载时的初始化操作
  if (!window._superMasterSetupDone) {
    window._superMasterSetupDone = true;
    setupEnterKeyNavigationWithObserver();
  }
  // 获取当前用户的采购单位并设置到表单
  getUserPurchasingUnits(formData, axios);
  // 加载历史地址
  fetchAddressOptions();
  // 获取型号列表
  fetchLensModels();
  // 获取验配单位列表
  fetchOptometryClinics();
  initFrameTypeByUser();
});

// 统一执行一次转换逻辑（用于Excel导入或手动批量修改后）
const runAllConversions = () => {
  // 双眼瞳距
  calculateBinocularPupilDistance(formData);

  // —— 对数视力 → 小数视力（方案B）——
  convertVisionFieldsInForm(formData);

  // —— 统一补全数值字段的小数位 ——
  const numericFieldRegex = /(Sphere|Cylinder|K1|K2|AxialLength|PupilHeight|PupilDistance|binocularPupilDistance)$/i;
  Object.keys(formData).forEach(key => {
    const val = formData[key];
    if (val !== null && val !== '' && !isNaN(val) && numericFieldRegex.test(key)) {
      const num = parseFloat(val);
      formData[key] = num.toFixed(2);
    }
  });
};

// 复制常规验光数据到超主点验光
const copyRegularToSuper = () => {
  // 复制右眼数据
  formData.superOdSphere = formData.regularOdSphere;
  formData.superOdCylinder = formData.regularOdCylinder;
  formData.superOdAxis = formData.regularOdAxis;
  formData.superOdVision = formData.regularOdVision;
  
  // 复制左眼数据
  formData.superOsSphere = formData.regularOsSphere;
  formData.superOsCylinder = formData.regularOsCylinder;
  formData.superOsAxis = formData.regularOsAxis;
  formData.superOsVision = formData.regularOsVision;
  
  // 复制双眼数据
  formData.superBinocularVision = formData.regularBinocularVision;
  formData.superBinocularFusion = formData.regularBinocularFusion;
  formData.superStereoscopicVisionFunction = formData.regularStereoscopicVisionFunction;
  
  ElMessage.success('已复制常规验光数据到超主点验光');
};

// 复制散瞳验光数据到常规验光
const copyCycloplegicToRegular = () => {
  formData.regularOdSphere = formData.cycloplegicOdSphere;
  formData.regularOdCylinder = formData.cycloplegicOdCylinder;
  formData.regularOdAxis = formData.cycloplegicOdAxis;
  formData.regularOdVision = formData.cycloplegicOdVision;
  formData.regularOsSphere = formData.cycloplegicOsSphere;
  formData.regularOsCylinder = formData.cycloplegicOsCylinder;
  formData.regularOsAxis = formData.cycloplegicOsAxis;
  formData.regularOsVision = formData.cycloplegicOsVision;

  ElMessage.success('已复制散瞳验光数据到常规验光');
};

// ============ 历史地址功能 ============
const addressOptions = ref([]);

// 获取用户历史地址
const fetchAddressOptions = async () => {
  const userId = localStorage.getItem('userId');
  if (!userId) return;
  try {
    const res = await axios.get('/api/v1/user/address', {
      params: { userId },
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    if (res.data.code === '200') {
      addressOptions.value = res.data.data || [];
    }
  } catch (e) {
    console.error('获取地址列表失败', e);
  }
};

// 自动补全查询
const queryAddressSuggestions = (queryString, cb) => {
  const keyword = queryString.trim();
  const results = addressOptions.value.filter(addr => {
    if (!keyword) return true;
    return (addr.addressee && addr.addressee.includes(keyword)) ||
           (addr.phone && addr.phone.includes(keyword)) ||
           (addr.shippingAddress && addr.shippingAddress.includes(keyword));
  }).map(addr => ({
    value: `${addr.addressee} ${addr.phone} ${addr.shippingAddress}`,
    addr
  }));
  cb(results);
};

// 选中自动补全项后回填
const handleAddressSelectSuggestion = (item) => {
  if (item && item.addr) {
    const addr = item.addr;
    formData.addressee = addr.addressee || '';
    formData.phone = addr.phone || '';
    formData.shippingAddress = addr.shippingAddress || '';
  }
};
// =====================================

</script>

<template>
  <div class="supermaster-container">
    <div class="page-header">
      <h1 class="page-title">超主点镜片订单</h1>
      <div class="order-steps">
        <div :class="['step', currentStep >= 1 ? 'active' : '']">填写订单 <div class="step-number">1</div></div>
        <div :class="['step', currentStep >= 2 ? 'active' : '']">确认信息 <div class="step-number">2</div></div>
        <div :class="['step', currentStep >= 3 ? 'active' : '']">提交成功 <div class="step-number">3</div></div>
      </div>
    </div>

    <!-- 步骤1: 填写订单表单 -->
    <div v-if="currentStep === 1" class="order-form">
      <!-- 订单基本信息 -->
      <div class="form-section">
        <h2 class="section-title">
          订单基本信息
          <el-button 
            type="success" 
            size="small" 
            style="float:right" 
            @click="triggerExcelUpload" 
            :loading="uploadInProgress"
            :disabled="uploadInProgress">
            {{ uploadInProgress ? '导入中...' : '导入Excel' }}
          </el-button>
        </h2>
        <div class="form-row">
          <div class="form-item">
            <label>订单日期<span class="required-star">*</span>：</label>
            <el-date-picker 
              v-model="formData.orderDate" 
              type="date" 
              placeholder="选择日期" 
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              :disabled="true"
              class="form-input" />
          </div>
          <div class="form-item">
            <label>患者姓名<span class="required-star">*</span>：</label>
            <el-input 
              v-model="formData.patientName" 
              placeholder="请输入患者姓名"
              maxlength="30"
              class="form-input" />
          </div>
          <div class="form-item">
            <label>年龄<span class="required-star">*</span>：</label>
            <el-input 
              v-model="formData.age" 
              placeholder="请输入年龄"
              maxlength="10"
              class="form-input" />
          </div>
          <div class="form-item">
            <label>验光师：</label>
            <el-input 
              v-model="formData.optometrist" 
              placeholder="请输入验光师"
              maxlength="10"
              class="form-input" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-item">
            <label>地区：</label>
            <el-select
              v-model="formData.region"
              placeholder="请选择地区"
              filterable
              clearable
              class="form-input">
              <el-option
                v-for="item in regionOptions"
                :key="item.value"
                :label="item.value"
                :value="item.value" />
            </el-select>
          </div>
          <div class="form-item">
            <label>订单型号<span class="required-star">*</span>：</label>
            <div style="flex: 1; display: flex; flex-direction: column; gap: 8px;">
              <el-select
                v-model="formData.lensModel"
                placeholder="请选择镜片型号"
                filterable
                clearable
                class="form-input">
                <el-option
                  v-for="item in lensModelOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value">
                </el-option>
              </el-select>
              <el-alert
                v-if="showModelAccuracyReminder"
                type="warning"
                :closable="false"
                show-icon
                class="model-accuracy-reminder">
                选中型号不准确，请联系商务人员，进行数据校验。
              </el-alert>
            </div>
          </div>
          <div class="form-item">
            <label>验配单位<span class="required-star">*</span>：</label>
            <div style="flex: 1; display: flex; flex-direction: column; gap: 8px;">
              <el-select
                v-model="formData.company"
                placeholder="请选择验配单位"
                filterable
                clearable
                class="form-input">
                <el-option
                  v-for="item in optometryClinicOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value">
                </el-option>
                <template #empty>
                  <div class="company-empty-tip">
                    <span>无匹配数据，请到</span>
                    <router-link to="/unit" class="company-empty-link">验配单位管理</router-link>
                    <span>页面新增验配单位</span>
                  </div>
                </template>
              </el-select>
              <el-alert
                v-if="showCompanyMismatchWarning"
                type="warning"
                :closable="false"
                show-icon
                class="model-accuracy-reminder">
                Excel验配单位“{{ excelOriginalCompany }}”未匹配，请手动校验后选择。
              </el-alert>
            </div>
          </div>
          <div class="form-item">
            <label>采购单位<span class="required-star">*</span>：</label>
            <el-input
              v-model="formData.purchasingUnits"
              placeholder="请输入采购单位"
              maxlength="30"
              class="form-input"
              :disabled="!isAdmin" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-item full-width">
            <label>型号备注<span v-if="isHiddenRemarksRequired" class="required-star">*</span>：</label>
            <el-input
              v-model="formData.hiddenRemarks"
              type="textarea"
              :placeholder="hiddenRemarksPlaceholder"
              show-word-limit
              class="form-input" />
          </div>
        </div>
      </div>

      <!-- 超主点β型号专属信息 -->
      <div v-if="isBetaStarModel" class="form-section beta-history-section">
        <h2 class="section-title">超主点β既往情况</h2>
        <div class="beta-history-grid">
          <div class="beta-history-item">
            <div class="beta-history-label">是否有使用阿托品滴眼液<span class="required-star">*</span></div>
            <el-radio-group v-model="formData.atropineEyeDropsUsed" class="beta-history-radio">
              <el-radio label="是">是</el-radio>
              <el-radio label="否">否</el-radio>
            </el-radio-group>
            <div v-if="showAtropineStopDuration" class="beta-history-duration">
              <div class="beta-history-sublabel">停药时长<span class="required-star">*</span></div>
              <div class="beta-history-duration-control">
                <el-select v-model="atropineStopMonths" placeholder="选择月数" clearable class="beta-history-duration-select">
                  <el-option v-for="option in durationMonthOptions" :key="`atropine-month-${option.value}`" :label="option.label" :value="option.value" />
                </el-select>
                <span class="beta-history-unit">个月</span>
                <el-select v-model="atropineStopDays" placeholder="选择天数" clearable class="beta-history-duration-select">
                  <el-option v-for="option in durationDayOptions" :key="`atropine-day-${option.value}`" :label="option.label" :value="option.value" />
                </el-select>
                <span class="beta-history-unit">天</span>
                <span v-if="formData.atropineEyeDropsStopDuration" class="beta-history-elapsed">已停药 {{ formData.atropineEyeDropsStopDuration }}</span>
              </div>
            </div>
          </div>
          <div class="beta-history-item">
            <div class="beta-history-label">是否配戴过角膜塑形镜（OK镜）<span class="required-star">*</span></div>
            <el-radio-group v-model="formData.orthokeratologyLensUsed" class="beta-history-radio">
              <el-radio label="是">是</el-radio>
              <el-radio label="否">否</el-radio>
            </el-radio-group>
            <div v-if="showOrthokeratologyStopDuration" class="beta-history-duration">
              <div class="beta-history-sublabel">停戴时长<span class="required-star">*</span></div>
              <div class="beta-history-duration-control">
                <el-select v-model="orthokeratologyStopMonths" placeholder="选择月数" clearable class="beta-history-duration-select">
                  <el-option v-for="option in durationMonthOptions" :key="`ok-month-${option.value}`" :label="option.label" :value="option.value" />
                </el-select>
                <span class="beta-history-unit">个月</span>
                <el-select v-model="orthokeratologyStopDays" placeholder="选择天数" clearable class="beta-history-duration-select">
                  <el-option v-for="option in durationDayOptions" :key="`ok-day-${option.value}`" :label="option.label" :value="option.value" />
                </el-select>
                <span class="beta-history-unit">天</span>
                <span v-if="formData.orthokeratologyLensStopDuration" class="beta-history-elapsed">已停戴 {{ formData.orthokeratologyLensStopDuration }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 验光数据部分 -->
      <div class="refraction-sections">
      <div class="form-section two-columns">
      <!-- 常规验光数据 -->
        <div class="column">
          <h2 class="section-title">
            常规验光
            <el-button
              type="primary"
              size="small"
              style="float:right"
              @click="copyCycloplegicToRegular">
              复制散瞳验光数据
            </el-button>
          </h2>
          <div class="eye-data-container">
            <div class="eye-section">
              <h3 class="eye-title">右眼</h3>
              <div class="form-row">
                <div class="form-item">
                  <label>球镜<span class="required-star">*</span>：</label>
                  <el-input 
                    v-model="formData.regularOdSphere" 
                    placeholder="请输入球镜，如+1.00"
                    maxlength="10"
                    @change="formData.regularOdSphere = validateNumericInput(formData.regularOdSphere, '球镜')"
                    class="form-input" />
                </div>
                <div class="form-item">
                  <label>柱镜<span class="required-star">*</span>：</label>
                  <el-input
                    v-model="formData.regularOdCylinder" 
                    placeholder="请输入柱镜，如-1.00"
                    maxlength="10"
                    @change="formData.regularOdCylinder = validateNumericInput(formData.regularOdCylinder, '柱镜')"
                    class="form-input" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-item">
                  <label>轴位<span class="required-star">*</span>：</label>
                  <el-input 
                    v-model="formData.regularOdAxis" 
                    placeholder="请输入轴位,如90"
                    maxlength="10"
                    @change="formData.regularOdAxis = validateNumericInput(formData.regularOdAxis, '轴位')"
                    class="form-input" />
                </div>
                <div class="form-item">
                  <label>视力<span class="required-star">*</span>：</label>
                  <el-input 
                    v-model="formData.regularOdVision" 
                    placeholder="请输入视力,如1.0"
                    maxlength="55"
                    class="form-input" />
                </div>
              </div>
            </div>
            <div class="eye-section">
              <h3 class="eye-title">左眼</h3>
              <div class="form-row">
                <div class="form-item">
                  <label>球镜<span class="required-star">*</span>：</label>
                  <el-input 
                    v-model="formData.regularOsSphere" 
                    placeholder="请输入球镜，如+1.00"
                    maxlength="10"
                    @change="formData.regularOsSphere = validateNumericInput(formData.regularOsSphere, '球镜')"
                    class="form-input" />
                </div>
                <div class="form-item">
                  <label>柱镜<span class="required-star">*</span>：</label>
                  <el-input 
                    v-model="formData.regularOsCylinder" 
                    placeholder="请输入柱镜，如-1.00"
                    maxlength="10"
                    @change="formData.regularOsCylinder = validateNumericInput(formData.regularOsCylinder, '柱镜')"
                    class="form-input" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-item">
                  <label>轴位<span class="required-star">*</span>：</label>
                  <el-input 
                    v-model="formData.regularOsAxis" 
                    placeholder="请输入轴位，如90"
                    maxlength="10"
                    @change="formData.regularOsAxis = validateNumericInput(formData.regularOsAxis, '轴位')"
                    class="form-input" />
                </div>
                <div class="form-item">
                  <label>视力<span class="required-star">*</span>：</label>
                  <el-input 
                    v-model="formData.regularOsVision" 
                    placeholder="请输入视力，如1.0"
                    maxlength="55"
                    class="form-input" />
                </div>
              </div>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>双眼融像：</label>
              <el-input
                  v-model="formData.regularBinocularFusion"
                  placeholder="请输入双眼融像"
                  maxlength="60"
                  class="form-input" />
            </div>
            <div class="form-item">
              <label style="display: inline-block; width: 90px;">立体视功能：</label>
              <el-input
                  v-model="formData.regularStereoscopicVisionFunction"
                  maxlength="60"
                  placeholder="请输入立体视功能"
                  class="form-input" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>双眼视力：</label>
              <el-input 
                v-model="formData.regularBinocularVision"
                maxlength="55"
                placeholder="请输入双眼视力" 
                class="form-input" />
            </div>
          </div>
        </div>
      </div>
      <!-- 散瞳验光数据 -->
      <div class="form-section two-columns cycloplegic-refraction-section">
        <div class="column">
          <h2 class="section-title">散瞳验光</h2>
          <div class="eye-data-container">
            <div class="eye-section">
              <h3 class="eye-title">右眼</h3>
              <div class="form-row">
                <div class="form-item">
                  <label>球镜：</label>
                  <el-input v-model="formData.cycloplegicOdSphere" placeholder="请输入球镜，如+1.00" maxlength="10" @change="formData.cycloplegicOdSphere = validateNumericInput(formData.cycloplegicOdSphere, '球镜')" class="form-input" />
                </div>
                <div class="form-item">
                  <label>柱镜：</label>
                  <el-input v-model="formData.cycloplegicOdCylinder" placeholder="请输入柱镜，如-1.00" maxlength="10" @change="formData.cycloplegicOdCylinder = validateNumericInput(formData.cycloplegicOdCylinder, '柱镜')" class="form-input" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-item">
                  <label>轴位：</label>
                  <el-input v-model="formData.cycloplegicOdAxis" placeholder="请输入轴位，如90" maxlength="10" @change="formData.cycloplegicOdAxis = validateNumericInput(formData.cycloplegicOdAxis, '轴位')" class="form-input" />
                </div>
                <div class="form-item">
                  <label>视力：</label>
                  <el-input v-model="formData.cycloplegicOdVision" placeholder="请输入视力，如1.0" maxlength="55" class="form-input" />
                </div>
              </div>
            </div>
            <div class="eye-section">
              <h3 class="eye-title">左眼</h3>
              <div class="form-row">
                <div class="form-item">
                  <label>球镜：</label>
                  <el-input v-model="formData.cycloplegicOsSphere" placeholder="请输入球镜，如+1.00" maxlength="10" @change="formData.cycloplegicOsSphere = validateNumericInput(formData.cycloplegicOsSphere, '球镜')" class="form-input" />
                </div>
                <div class="form-item">
                  <label>柱镜：</label>
                  <el-input v-model="formData.cycloplegicOsCylinder" placeholder="请输入柱镜，如-1.00" maxlength="10" @change="formData.cycloplegicOsCylinder = validateNumericInput(formData.cycloplegicOsCylinder, '柱镜')" class="form-input" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-item">
                  <label>轴位：</label>
                  <el-input v-model="formData.cycloplegicOsAxis" placeholder="请输入轴位，如90" maxlength="10" @change="formData.cycloplegicOsAxis = validateNumericInput(formData.cycloplegicOsAxis, '轴位')" class="form-input" />
                </div>
                <div class="form-item">
                  <label>视力：</label>
                  <el-input v-model="formData.cycloplegicOsVision" placeholder="请输入视力，如1.0" maxlength="55" class="form-input" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- 超主点验光数据 -->
      <div class="form-section two-columns trial-refraction-section">
        <div class="column">
          <h2 class="section-title">
            超主点验光
            <el-button 
              type="primary" 
              size="small" 
              style="float:right"
              @click="copyRegularToSuper">
              复制常规验光数据
            </el-button>
          </h2>
          <div class="eye-data-container">
            <div class="eye-section">
              <h3 class="eye-title">右眼</h3>
              <div class="form-row">
                <div class="form-item">
                  <label>球镜<span class="required-star">*</span>：</label>
                  <el-input
                      v-model="formData.superOdSphere"
                      placeholder="请输入球镜，如+1.00"
                      maxlength="10"
                      @change="formData.superOdSphere = validateNumericInput(formData.superOdSphere, '球镜')"
                      class="form-input" />
                </div>
                <div class="form-item">
                  <label>柱镜<span class="required-star">*</span>：</label>
                  <el-input
                      v-model="formData.superOdCylinder"
                      placeholder="请输入柱镜，如-1.00"
                      maxlength="10"
                      @change="formData.superOdCylinder = validateNumericInput(formData.superOdCylinder, '柱镜')"
                      class="form-input" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-item">
                  <label>轴位<span class="required-star">*</span>：</label>
                  <el-input
                      v-model="formData.superOdAxis"
                      placeholder="请输入轴位，如90"
                      maxlength="10"
                      @change="formData.superOdAxis = validateNumericInput(formData.superOdAxis, '轴位')"
                      class="form-input" />
                </div>
                <div class="form-item">
                  <label>视力<span class="required-star">*</span>：</label>
                  <el-input
                      v-model="formData.superOdVision"
                      placeholder="请输入视力，如1.0"
                      maxlength="55"
                      class="form-input" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-item">
                  <label>数量：</label>
                  <el-input
                      v-model="formData.odQuantity"
                      placeholder="请输入数量"
                      maxlength="10"
                      @change="formData.odQuantity = validateNumericInput(formData.odQuantity, '数量')"
                      class="form-input" />
                </div>
              </div>
            </div>
            <div class="eye-section">
              <h3 class="eye-title">左眼</h3>
              <div class="form-row">
                <div class="form-item">
                  <label>球镜<span class="required-star">*</span>：</label>
                  <el-input
                      v-model="formData.superOsSphere"
                      placeholder="请输入球镜，如+1.00"
                      maxlength="10"
                      @change="formData.superOsSphere = validateNumericInput(formData.superOsSphere, '球镜')"
                      class="form-input" />
                </div>
                <div class="form-item">
                  <label>柱镜<span class="required-star">*</span>：</label>
                  <el-input
                      v-model="formData.superOsCylinder"
                      placeholder="请输入柱镜，如-1.00"
                      maxlength="10"
                      @change="formData.superOsCylinder = validateNumericInput(formData.superOsCylinder, '柱镜')"
                      class="form-input" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-item">
                  <label>轴位<span class="required-star">*</span>：</label>
                  <el-input
                      v-model="formData.superOsAxis"
                      placeholder="请输入轴位，如90"
                      maxlength="10"
                      @change="formData.superOsAxis = validateNumericInput(formData.superOsAxis, '轴位')"
                      class="form-input" />
                </div>
                <div class="form-item">
                  <label>视力<span class="required-star">*</span>：</label>
                  <el-input
                      v-model="formData.superOsVision"
                      maxlength="55"
                      placeholder="请输入视力，如1.0"
                      class="form-input" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-item">
                  <label>数量：</label>
                  <el-input
                      v-model="formData.osQuantity"
                      placeholder="请输入数量"
                      maxlength="10"
                      @change="formData.osQuantity = validateNumericInput(formData.osQuantity, '数量')"
                      class="form-input" />
                </div>
              </div>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>双眼融像：</label>
              <el-input
                  v-model="formData.superBinocularFusion"
                  placeholder="请输入双眼融像"
                  maxlength="60"
                  class="form-input" />
            </div>
            <div class="form-item">
              <label style="display: inline-block; width: 90px;">立体视功能：</label>
              <el-input
                  v-model="formData.superStereoscopicVisionFunction"
                  maxlength="60"
                  placeholder="请输入立体视功能"
                  class="form-input" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>双眼视力：</label>
              <el-input
                  v-model="formData.superBinocularVision"
                  maxlength="55"
                  placeholder="请输入双眼视力"
                  class="form-input" />
            </div>
          </div>
      </div>
    </div>
      </div>

      <!-- 角膜曲率与眼镜参数 -->
      <div class="form-section two-columns">
        <!-- 角膜曲率 -->
        <div class="column">
          <h2 class="section-title">角膜曲率</h2>
          <div class="eye-data-container">
            <div class="eye-section">
              <h3 class="eye-title">右眼</h3>
              <div class="form-row">
                <div class="form-item">
                  <label style="display: inline-block; width: 90px;">K1<span class="required-star">*</span>：</label>
                  <el-input 
                    v-model="formData.odK1" 
                    placeholder="请输入K1"
                    maxlength="10"
                    @change="formData.odK1 = validateNumericInput(formData.odK1, 'K1')"
                    class="form-input" />
                </div>
                <div class="form-item">
                  <label style="display: inline-block; width: 90px;">K2<span class="required-star">*</span>：</label>
                  <el-input
                    v-model="formData.odK2" 
                    placeholder="请输入K2"
                    maxlength="10"
                    @change="formData.odK2 = validateNumericInput(formData.odK2, 'K2')"
                    class="form-input" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-item">
                  <label style="display: inline-block; width: 90px;">眼轴<span class="required-star">*</span>：</label>
                  <el-input
                    v-model="formData.odAxialLength" 
                    placeholder="请输入眼轴"
                    maxlength="10"
                    @change="formData.odAxialLength = validateNumericInput(formData.odAxialLength, '眼轴')"
                    class="form-input" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-item">
                  <label style="display: inline-block; width: 90px;">脉络膜厚度：</label>
                  <el-input
                      v-model="formData.odChoroidalThickness"
                      placeholder="请输入脉络膜厚度"
                      maxlength="10"
                      @change="formData.odChoroidalThickness = validateNumericInput(formData.odChoroidalThickness, '脉络膜厚度')"
                      class="form-input"/>
                </div>
              </div>
            </div>
            <div class="eye-section">
              <h3 class="eye-title">左眼</h3>
              <div class="form-row">
                <div class="form-item">
                  <label style="display: inline-block; width: 90px;">K1<span class="required-star">*</span>：</label>
                  <el-input 
                    v-model="formData.osK1" 
                    placeholder="请输入K1"
                    maxlength="10"
                    @change="formData.osK1 = validateNumericInput(formData.osK1, 'K1')" 
                    class="form-input" />
                </div>
                <div class="form-item">
                  <label style="display: inline-block; width: 90px;">K2<span class="required-star">*</span>：</label>
                  <el-input 
                    v-model="formData.osK2"
                    maxlength="10"
                    placeholder="请输入K2" 
                    @change="formData.osK2 = validateNumericInput(formData.osK2, 'K2')"
                    class="form-input" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-item">
                  <label style="display: inline-block; width: 90px;">眼轴<span class="required-star">*</span>：</label>
                  <el-input
                      v-model="formData.osAxialLength"
                      placeholder="请输入眼轴"
                      maxlength="10"
                      @change="formData.osAxialLength = validateNumericInput(formData.osAxialLength, '眼轴')"
                      class="form-input" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-item">
                  <label style="display: inline-block; width: 90px;">脉络膜厚度：</label>
                  <el-input
                      v-model="formData.osChoroidalThickness"
                      placeholder="请输入脉络膜厚度"
                      maxlength="10"
                      @change="formData.osChoroidalThickness = validateNumericInput(formData.osChoroidalThickness, '脉络膜厚度')"
                      class="form-input"/>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="form-section two-columns">
        <!-- 眼镜参数 -->
        <div class="column">
          <h2 class="section-title">眼镜参数</h2>
          <div class="eye-data-container">
            <div class="eye-section">
              <h3 class="eye-title">右眼</h3>
              <div class="form-row">
                <div class="form-item">
                  <label>瞳高<span class="required-star">*</span>：</label>
                  <el-input
                      v-model="formData.odPupilHeight"
                      placeholder="请输入瞳高"
                      maxlength="10"
                      @change="formData.odPupilHeight = validateNumericInput(formData.odPupilHeight, '瞳高')"
                      class="form-input" />
                </div>
                <div class="form-item">
                  <label>瞳距<span class="required-star">*</span>：</label>
                  <el-input
                      v-model="formData.odPupilDistance"
                      maxlength="10"
                      placeholder="请输入右眼瞳距"
                      @change="formData.odPupilDistance = validateNumericInput(formData.odPupilDistance, '瞳距'); calculateBinocularPupilDistance(formData)"
                      class="form-input" />
                </div>
              </div>
            </div>
            <div class="eye-section">
              <h3 class="eye-title">左眼</h3>
              <div class="form-row">
                <div class="form-item">
                  <label>瞳高<span class="required-star">*</span>：</label>
                  <el-input
                      v-model="formData.osPupilHeight"
                      placeholder="请输入瞳高"
                      maxlength="10"
                      @change="formData.osPupilHeight = validateNumericInput(formData.osPupilHeight, '瞳高')"
                      class="form-input" />
                </div>
                <div class="form-item">
                  <label>瞳距<span class="required-star">*</span>：</label>
                  <el-input
                      v-model="formData.osPupilDistance"
                      placeholder="请输入左眼瞳距"
                      maxlength="10"
                      @change="formData.osPupilDistance = validateNumericInput(formData.osPupilDistance, '瞳距'); calculateBinocularPupilDistance(formData)"
                      class="form-input" />
                </div>
              </div>
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>双眼瞳距：</label>
              <el-input
                  v-model="formData.binocularPupilDistance"
                  maxlength="10"
                  placeholder="自动计算"
                  class="form-input" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>镜框编号<span v-if="isFrameNumberRequired" class="required-star">*</span>：</label>
              <el-input
                  v-model="formData.frameNumber"
                  maxlength="30"
                  placeholder="请输入镜框编号"
                  class="form-input" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>镜框类型<span class="required-star">*</span>：</label>
              <el-radio-group v-model="formData.frameType">
                <el-radio v-if="showOriginalFrameOption" label="原片">原片</el-radio>
                <el-radio label="来框加工">来框加工</el-radio>
                <el-radio label="豪赋镜框加工">豪赋镜框加工</el-radio>
              </el-radio-group>
            </div>
          </div>
        </div>
      </div>

      <!-- 邮寄信息 -->
      <div class="form-section">
        <h2 class="section-title">邮寄信息</h2>
        <div class="form-row">
          <div class="form-item">
            <label>收件人<span class="required-star">*</span>：</label>
            <el-autocomplete
              v-model="formData.addressee"
              :fetch-suggestions="queryAddressSuggestions"
              placeholder="请输入收件人姓名"
              maxlength="20"
              :highlight-first-item="true"
              @select="handleAddressSelectSuggestion"
              class="form-input" />
          </div>
          <div class="form-item">
            <label>联系电话<span class="required-star">*</span>：</label>
            <el-input 
              v-model="formData.phone"
              placeholder="请输入联系电话"
              maxlength="15"
              class="form-input" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-item full-width">
            <label>邮寄地址<span class="required-star">*</span>：</label>
            <el-input 
              v-model="formData.shippingAddress" 
              type="textarea" 
              :rows="2" 
              placeholder="请输入详细邮寄地址" 
              class="form-input" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-item full-width">
            <label>备注：</label>
            <el-input 
              v-model="formData.remarks" 
              type="textarea" 
              :rows="2" 
              placeholder="请输入备注信息" 
              class="form-input" />
          </div>
        </div>
        <span>带<span class="required-star">*</span>为必填项</span>
      </div>

      <!-- 操作按钮 -->
      <div class="form-actions">
        <el-button @click="resetForm">重置</el-button>
        <el-button type="primary" @click="handlePreviewSubmit">提交订单</el-button>
      </div>
    </div>

    <!-- 步骤2: 确认信息 -->
    <div v-if="currentStep === 2" class="order-form">
      <div class="form-section">
        <h2 class="section-title">订单确认信息</h2>
        <p class="confirmation-message">请确认以下信息是否正确，如需修改请点击"返回编辑"按钮</p>
        
        <!-- 订单基本信息预览 -->
        <div class="preview-section">
          <h3 class="preview-title">订单基本信息</h3>
          <div class="preview-content">
            <div class="preview-row">
              <div class="preview-item">
                <span class="preview-label">订单日期<span class="required-star">*</span>：</span>
                <span class="preview-value">{{ formData.orderDate }}</span>
              </div>
              <div class="preview-item">
                <span class="preview-label">患者姓名<span class="required-star">*</span>：</span>
                <span class="preview-value">{{ formData.patientName }}</span>
              </div>
              <div class="preview-item">
                <span class="preview-label">年龄<span class="required-star">*</span>：</span>
                <span class="preview-value">{{ formData.age }}</span>
              </div>
              <div class="preview-item">
                <span class="preview-label">验光师：</span>
                <span class="preview-value">{{ formData.optometrist }}</span>
              </div>
            </div>
            <div class="preview-row">
              <div class="preview-item">
                <span class="preview-label">地区：</span>
                <span class="preview-value">{{ formData.region }}</span>
              </div>
              <div class="preview-item">
                <span class="preview-label">订单型号<span class="required-star">*</span>：</span>
                <span class="preview-value">{{ formData.lensModel }}</span>
              </div>
              <div class="preview-item">
                <span class="preview-label">验配单位<span class="required-star">*</span>：</span>
                <span class="preview-value">{{ formData.company }}</span>
              </div>
              <div class="preview-item">
                <span class="preview-label">采购单位<span class="required-star">*</span>：</span>
                <span class="preview-value">{{ formData.purchasingUnits }}</span>
              </div>
            </div>
            <div v-if="isBetaStarModel" class="preview-row">
              <div class="preview-item">
                <span class="preview-label">使用阿托品滴眼液：</span>
                <span class="preview-value">{{ formData.atropineEyeDropsUsed }}</span>
              </div>
              <div v-if="showAtropineStopDuration" class="preview-item">
                <span class="preview-label">阿托品停药时长：</span>
                <span class="preview-value">{{ formData.atropineEyeDropsStopDuration }}</span>
              </div>
              <div class="preview-item">
                <span class="preview-label">配戴过角膜塑形镜（OK镜）：</span>
                <span class="preview-value">{{ formData.orthokeratologyLensUsed }}</span>
              </div>
              <div v-if="showOrthokeratologyStopDuration" class="preview-item">
                <span class="preview-label">OK镜停戴时长：</span>
                <span class="preview-value">{{ formData.orthokeratologyLensStopDuration }}</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 验光数据预览 -->
        <div class="refraction-preview-sections">
        <div class="preview-section">
          <div class="preview-two-columns">
            <!-- 常规验光数据预览 -->
            <div class="preview-column">
              <h3 class="preview-title">常规验光</h3>
              <div class="preview-eye-container">
                <div class="preview-eye">
                  <h4 class="preview-eye-title">右眼</h4>
                  <div class="preview-row compact">
                    <div class="preview-item">
                      <span class="preview-label">球镜<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.regularOdSphere }}</span>
                    </div>
                    <div class="preview-item">
                      <span class="preview-label">柱镜<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.regularOdCylinder }}</span>
                    </div>
                  </div>
                  <div class="preview-row compact">
                    <div class="preview-item">
                      <span class="preview-label">轴位<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.regularOdAxis }}</span>
                    </div>
                    <div class="preview-item">
                      <span class="preview-label">视力<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.regularOdVision }}</span>
                    </div>
                  </div>
                </div>
                <div class="preview-eye">
                  <h4 class="preview-eye-title">左眼</h4>
                  <div class="preview-row compact">
                    <div class="preview-item">
                      <span class="preview-label">球镜<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.regularOsSphere }}</span>
                    </div>
                    <div class="preview-item">
                      <span class="preview-label">柱镜<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.regularOsCylinder }}</span>
                    </div>
                  </div>
                  <div class="preview-row compact">
                    <div class="preview-item">
                      <span class="preview-label">轴位<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.regularOsAxis }}</span>
                    </div>
                    <div class="preview-item">
                      <span class="preview-label">视力<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.regularOsVision }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="preview-row">
                <div class="preview-item">
                  <span class="preview-label">双眼融像：</span>
                  <span class="preview-value">{{ formData.regularBinocularFusion }}</span>
                </div>
                <div class="preview-item">
                  <span class="preview-label" style="display: inline-block; width: 90px;">立体视功能：</span>
                  <span class="preview-value">{{ formData.regularStereoscopicVisionFunction }}</span>
                </div>
              </div>

              <div class="preview-row">
                <div class="preview-item">
                  <span class="preview-label">双眼视力：</span>
                  <span class="preview-value">{{ formData.regularBinocularVision }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="preview-section cycloplegic-refraction-section">
          <div class="preview-two-columns">
            <div class="preview-column">
              <h3 class="preview-title">散瞳验光</h3>
              <div class="preview-eye-container">
                <div class="preview-eye">
                  <h4 class="preview-eye-title">右眼</h4>
                  <div class="preview-row compact"><div class="preview-item"><span class="preview-label">球镜：</span><span class="preview-value">{{ formData.cycloplegicOdSphere }}</span></div><div class="preview-item"><span class="preview-label">柱镜：</span><span class="preview-value">{{ formData.cycloplegicOdCylinder }}</span></div></div>
                  <div class="preview-row compact"><div class="preview-item"><span class="preview-label">轴位：</span><span class="preview-value">{{ formData.cycloplegicOdAxis }}</span></div><div class="preview-item"><span class="preview-label">视力：</span><span class="preview-value">{{ formData.cycloplegicOdVision }}</span></div></div>
                </div>
                <div class="preview-eye">
                  <h4 class="preview-eye-title">左眼</h4>
                  <div class="preview-row compact"><div class="preview-item"><span class="preview-label">球镜：</span><span class="preview-value">{{ formData.cycloplegicOsSphere }}</span></div><div class="preview-item"><span class="preview-label">柱镜：</span><span class="preview-value">{{ formData.cycloplegicOsCylinder }}</span></div></div>
                  <div class="preview-row compact"><div class="preview-item"><span class="preview-label">轴位：</span><span class="preview-value">{{ formData.cycloplegicOsAxis }}</span></div><div class="preview-item"><span class="preview-label">视力：</span><span class="preview-value">{{ formData.cycloplegicOsVision }}</span></div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="preview-section trial-refraction-section">
          <div class="preview-two-columns">
            <!-- 超主点验光数据预览 -->
            <div class="preview-column">
              <h3 class="preview-title">超主点验光</h3>
              <div class="preview-eye-container">
                <div class="preview-eye">
                  <h4 class="preview-eye-title">右眼</h4>
                  <div class="preview-row compact">
                    <div class="preview-item">
                      <span class="preview-label">球镜<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.superOdSphere }}</span>
                    </div>
                    <div class="preview-item">
                      <span class="preview-label">柱镜<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.superOdCylinder }}</span>
                    </div>
                  </div>
                  <div class="preview-row compact">
                    <div class="preview-item">
                      <span class="preview-label">轴位<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.superOdAxis }}</span>
                    </div>
                    <div class="preview-item">
                      <span class="preview-label">视力<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.superOdVision }}</span>
                    </div>
                  </div>
                  <div class="preview-row compact">
                    <div class="preview-item">
                      <span class="preview-label">数量：</span>
                      <span class="preview-value">{{ formData.odQuantity }}</span>
                    </div>
                  </div>
                </div>
                <div class="preview-eye">
                  <h4 class="preview-eye-title">左眼</h4>
                  <div class="preview-row compact">
                    <div class="preview-item">
                      <span class="preview-label">球镜<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.superOsSphere }}</span>
                    </div>
                    <div class="preview-item">
                      <span class="preview-label">柱镜<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.superOsCylinder }}</span>
                    </div>
                  </div>
                  <div class="preview-row compact">
                    <div class="preview-item">
                      <span class="preview-label">轴位<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.superOsAxis }}</span>
                    </div>
                    <div class="preview-item">
                      <span class="preview-label">视力<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.superOsVision }}</span>
                    </div>
                  </div>
                  <div class="preview-row compact">
                    <div class="preview-item">
                      <span class="preview-label">数量：</span>
                      <span class="preview-value">{{ formData.osQuantity }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="preview-row">
                <div class="preview-item">
                  <span class="preview-label">双眼融像：</span>
                  <span class="preview-value">{{ formData.superBinocularFusion }}</span>
                </div>
                <div class="preview-item">
                  <span class="preview-label" style="display: inline-block; width: 90px;">立体视功能：</span>
                  <span class="preview-value">{{ formData.superStereoscopicVisionFunction }}</span>
                </div>
              </div>
              <div class="preview-row">
                <div class="preview-item">
                  <span class="preview-label">双眼视力：</span>
                  <span class="preview-value">{{ formData.superBinocularVision }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        </div>
        <!-- 角膜曲率与眼镜参数预览 -->
        <div class="preview-section">
          <div class="preview-two-columns">
            <!-- 角膜曲率预览 -->
            <div class="preview-column">
              <h3 class="preview-title">角膜曲率</h3>
              <div class="preview-eye-container">
                <div class="preview-eye">
                  <h4 class="preview-eye-title">右眼</h4>
                  <div class="preview-row compact">
                    <div class="preview-item">
                      <span class="preview-label">K1<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.odK1 }}</span>
                    </div>
                    <div class="preview-item">
                      <span class="preview-label">K2<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.odK2 }}</span>
                    </div>
                  </div>
                  <div class="preview-row">
                    <div class="preview-item">
                      <span class="preview-label">眼轴<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.odAxialLength }}</span>
                    </div>
                    <div class="preview-item">
                      <span class="preview-label" style="display: inline-block; width: 90px;">脉络膜厚度：</span>
                      <span class="preview-value">{{ formData.odChoroidalThickness }}</span>
                    </div>
                  </div>
                </div>
                <div class="preview-eye">
                  <h4 class="preview-eye-title">左眼</h4>
                  <div class="preview-row compact">
                    <div class="preview-item">
                      <span class="preview-label">K1<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.osK1 }}</span>
                    </div>
                    <div class="preview-item">
                      <span class="preview-label">K2<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.osK2 }}</span>
                    </div>
                  </div>
                  <div class="preview-row">
                    <div class="preview-item">
                      <span class="preview-label">眼轴<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.osAxialLength }}</span>
                    </div>
                    <div class="preview-item">
                      <span class="preview-label" style="display: inline-block; width: 90px;">脉络膜厚度：</span>
                      <span class="preview-value">{{ formData.osChoroidalThickness }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="form-section">
          <div class="preview-two-columns">
            <!-- 眼镜参数预览 -->
            <div class="preview-column">
              <h3 class="preview-title">眼镜参数</h3>
              <div class="preview-eye-container">
                <div class="preview-eye">
                  <h4 class="preview-eye-title">右眼</h4>
                  <div class="preview-row compact">
                    <div class="preview-item">
                      <span class="preview-label">瞳高<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.odPupilHeight }}</span>
                    </div>
                    <div class="preview-item">
                      <span class="preview-label">瞳距<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.odPupilDistance }}</span>
                    </div>
                  </div>
                </div>
                <div class="preview-eye">
                  <h4 class="preview-eye-title">左眼</h4>
                  <div class="preview-row compact">
                    <div class="preview-item">
                      <span class="preview-label">瞳高<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.osPupilHeight }}</span>
                    </div>
                    <div class="preview-item">
                      <span class="preview-label">瞳距<span class="required-star">*</span>：</span>
                      <span class="preview-value">{{ formData.osPupilDistance }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="preview-row">
                <div class="preview-item">
                  <span class="preview-label">双眼瞳距：</span>
                  <span class="preview-value">{{ formData.binocularPupilDistance }}</span>
                </div>
              </div>
              <div class="preview-row">
                <div class="preview-item">
                  <span class="preview-label">镜框编号：</span>
                  <span class="preview-value">{{ formData.frameNumber }}</span>
                </div>
                <div class="preview-item">
                  <span class="preview-label">镜框类型：</span>
                  <span class="preview-value">{{ formData.frameType }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        
        <!-- 快递信息预览 -->
        <div class="preview-section">
          <h3 class="preview-title">快递信息</h3>
          <div class="preview-content">
            <div class="preview-row">
              <div class="preview-item">
                <span class="preview-label">收件人<span class="required-star">*</span>：</span>
                <span class="preview-value">{{ formData.addressee }}</span>
              </div>
              <div class="preview-item">
                <span class="preview-label">联系电话<span class="required-star">*</span>：</span>
                <span class="preview-value">{{ formData.phone }}</span>
              </div>
            </div>
            <div class="preview-row">
              <div class="preview-item full-width">
                <span class="preview-label">邮寄地址<span class="required-star">*</span>：</span>
                <span class="preview-value">{{ formData.shippingAddress }}</span>
              </div>
            </div>
            <div class="preview-row">
              <div class="preview-item full-width">
                <span class="preview-label">备注：</span>
                <span class="preview-value">{{ formData.remarks }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 操作按钮 -->
      <div class="form-actions">
        <el-button @click="backToEdit">返回编辑</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="isSubmitting" :disabled="isSubmitting">
          {{ isSubmitting.value ? '提交中...' : '确认提交' }}
        </el-button>
      </div>
    </div>

    <!-- 步骤3: 提交成功 -->
    <div v-if="currentStep === 3" class="order-form">
      <div class="form-section success-section">
        <div class="success-icon">
          <i class="el-icon-check"></i>
        </div>
        <h2 class="section-title">订单提交成功</h2>
        <p class="success-message">您的超主点镜片订单已成功提交，感谢您的订购！</p>
        
        <!-- 操作按钮 -->
        <div class="form-actions">
          <el-button type="primary" @click="startNewOrder">继续下单</el-button>
        </div>
      </div>
    </div>

    <el-dialog
      v-model="betaHistoryDialogVisible"
      title="超主点β既往情况"
      width="680px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      class="beta-history-dialog">
      <p class="beta-history-dialog-note">{{ betaHistoryDialogMessage }}，请补充后再次提交。</p>
      <div class="beta-history-grid beta-history-dialog-grid">
        <div class="beta-history-item">
          <div class="beta-history-label">是否有使用阿托品滴眼液<span class="required-star">*</span></div>
          <el-radio-group v-model="formData.atropineEyeDropsUsed" class="beta-history-radio">
            <el-radio label="是">是</el-radio>
            <el-radio label="否">否</el-radio>
          </el-radio-group>
          <div v-if="showAtropineStopDuration" class="beta-history-duration">
            <div class="beta-history-sublabel">停药时长<span class="required-star">*</span></div>
            <div class="beta-history-duration-control">
              <el-select v-model="atropineStopMonths" placeholder="选择月数" clearable class="beta-history-duration-select">
                <el-option v-for="option in durationMonthOptions" :key="`dialog-atropine-month-${option.value}`" :label="option.label" :value="option.value" />
              </el-select>
              <span class="beta-history-unit">个月</span>
              <el-select v-model="atropineStopDays" placeholder="选择天数" clearable class="beta-history-duration-select">
                <el-option v-for="option in durationDayOptions" :key="`dialog-atropine-day-${option.value}`" :label="option.label" :value="option.value" />
              </el-select>
              <span class="beta-history-unit">天</span>
            </div>
          </div>
        </div>
        <div class="beta-history-item">
          <div class="beta-history-label">是否配戴过角膜塑形镜（OK镜）<span class="required-star">*</span></div>
          <el-radio-group v-model="formData.orthokeratologyLensUsed" class="beta-history-radio">
            <el-radio label="是">是</el-radio>
            <el-radio label="否">否</el-radio>
          </el-radio-group>
          <div v-if="showOrthokeratologyStopDuration" class="beta-history-duration">
            <div class="beta-history-sublabel">停戴时长<span class="required-star">*</span></div>
            <div class="beta-history-duration-control">
              <el-select v-model="orthokeratologyStopMonths" placeholder="选择月数" clearable class="beta-history-duration-select">
                <el-option v-for="option in durationMonthOptions" :key="`dialog-ok-month-${option.value}`" :label="option.label" :value="option.value" />
              </el-select>
              <span class="beta-history-unit">个月</span>
              <el-select v-model="orthokeratologyStopDays" placeholder="选择天数" clearable class="beta-history-duration-select">
                <el-option v-for="option in durationDayOptions" :key="`dialog-ok-day-${option.value}`" :label="option.label" :value="option.value" />
              </el-select>
              <span class="beta-history-unit">天</span>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <el-button type="primary" @click="betaHistoryDialogVisible = false">完成填写</el-button>
      </template>
    </el-dialog>


    <!-- THIRD_EDIT: 在 page-header 之后插入隐藏文件输入 -->
    <input type="file" ref="excelFileInput" accept=".xlsx,.xls" @change="handleExcelUpload" style="display:none" />
  </div>
</template>

<style scoped>
.supermaster-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}

.page-title {
  font-size: 24px;
  font-weight: bold;
  color: #303133;
  margin: 0;
}

.order-steps {
  display: flex;
  align-items: center;
}

.step {
  position: relative;
  display: flex;
  align-items: center;
  margin-right: 40px;
  color: #909399;
  font-size: 14px;
}

.step:last-child {
  margin-right: 0;
}

.step:not(:last-child)::after {
  content: '';
  position: absolute;
  right: -25px;
  top: 50%;
  width: 10px;
  height: 1px;
  background-color: #dcdfe6;
}

.step.active {
  color: #409EFF;
  font-weight: bold;
}

.step-number {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #dcdfe6;
  color: white;
  font-size: 12px;
  margin-left: 8px;
}

.step.active .step-number {
  background-color: #409EFF;
}

.order-form {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.form-section {
  padding: 24px;
  margin-bottom: 24px;
  border-bottom: 1px solid #ebeef5;
}

.refraction-sections,
.refraction-preview-sections {
  display: flex;
  flex-direction: column;
}

.refraction-sections > .form-section:first-child,
.refraction-preview-sections > .preview-section:first-child {
  order: 2;
}

.refraction-sections > .cycloplegic-refraction-section,
.refraction-preview-sections > .cycloplegic-refraction-section {
  order: 1;
}

.refraction-sections > .trial-refraction-section,
.refraction-preview-sections > .trial-refraction-section {
  order: 3;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.section-title {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
  margin: 0 0 16px 0;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

/* 超主点β型号的既往情况：问题成组展示，避免长标签挤压控件 */
.beta-history-section {
  padding: 20px 24px 24px;
  border: 1px solid #dce9f5;
  border-radius: 8px;
  background: #f8fbff;
  box-shadow: 0 3px 10px rgba(64, 158, 255, 0.08);
}

.beta-history-dialog-note {
  margin: 0 0 16px;
  color: #606266;
  line-height: 1.6;
}

.beta-history-dialog-grid {
  margin: 0;
  border: 1px solid #dce9f5;
  border-radius: 4px;
}

.beta-history-section .section-title {
  color: #24557d;
  border-bottom-color: #cfe0ef;
}

.beta-history-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.beta-history-item {
  min-width: 0;
  padding: 16px 18px;
  border: 1px solid #dfe8f1;
  border-left: 3px solid #409eff;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(31, 78, 121, 0.05);
}

.beta-history-label {
  color: #303133;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.6;
}

.beta-history-radio {
  display: flex;
  gap: 24px;
  margin-top: 12px;
}

.beta-history-radio :deep(.el-radio) {
  margin-right: 0;
}

.beta-history-duration {
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed #dfe8f1;
}

.beta-history-sublabel {
  color: #606266;
  font-size: 13px;
  white-space: nowrap;
}

.beta-history-duration .form-input {
  min-width: 0;
}

.beta-history-duration-control {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.beta-history-duration-select {
  width: 132px;
}

.beta-history-unit {
  color: #606266;
  font-size: 13px;
}

.beta-history-elapsed {
  flex-basis: 100%;
  color: #409eff;
  font-size: 12px;
  line-height: 1.4;
}

@media screen and (max-width: 768px) {
  .beta-history-grid {
    grid-template-columns: 1fr;
  }

  .beta-history-duration-select {
    flex: 1 1 120px;
    width: auto;
  }
}

.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-item {
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
}

.form-item.full-width {
  flex-basis: 100%;
  min-width: 100%;
}

.form-item label {
  width: 80px;
  flex-shrink: 0;
  text-align: right;
  padding-right: 12px;
  font-size: 14px;
  color: #606266;
}

.form-input {
  flex: 1;
}

.model-accuracy-reminder {
  margin-top: 4px;
}

.two-columns {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}

.column {
  flex: 1;
  min-width: 300px;
}

.eye-data-container {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.eye-section {
  flex: 1;
  padding: 16px;
  border-radius: 4px;
  background-color: #f5f7fa;
}

.eye-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
  margin: 0 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 1px dashed #dcdfe6;
}

.form-actions {
  display: flex;
  justify-content: center;
  padding: 24px;
  gap: 16px;
}

/* 预览页面样式 */
.confirmation-message {
  margin-bottom: 20px;
  color: #606266;
  font-size: 14px;
  line-height: 1.6;
}

.preview-section {
  margin-bottom: 30px;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  padding: 16px;
}

.preview-title {
  font-size: 16px;
  font-weight: bold;
  color: #409EFF;
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
}

.preview-content {
  padding: 8px;
}

.preview-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}

.preview-row.compact {
  margin-bottom: 8px;
}

.preview-row:last-child {
  margin-bottom: 0;
}

.preview-item {
  flex: 1;
  min-width: 200px;
  display: flex;
}

.preview-item.full-width {
  flex-basis: 100%;
  min-width: 100%;
}

.preview-label {
  width: 80px;
  flex-shrink: 0;
  color: #909399;
  font-size: 14px;
}

.preview-value {
  flex: 1;
  color: #303133;
  font-size: 14px;
  font-weight: 500;
}

.preview-two-columns {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}

.preview-column {
  flex: 1;
  min-width: 300px;
}

.preview-eye-container {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.preview-eye {
  flex: 1;
  padding: 12px;
  border-radius: 4px;
  background-color: #f8f9fa;
}

.preview-eye-title {
  font-size: 14px;
  font-weight: bold;
  color: #606266;
  margin: 0 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 1px dashed #dcdfe6;
}

/* 成功页面样式 */
.success-section {
  text-align: center;
  padding: 40px 20px;
}

.success-icon {
  font-size: 64px;
  color: #67c23a;
  margin-bottom: 20px;
}

.success-icon i {
  background-color: #f0f9eb;
  border-radius: 50%;
  padding: 16px;
}

.success-message {
  font-size: 16px;
  color: #606266;
  margin-bottom: 30px;
}

/* 响应式调整 - 平板设备 */
@media screen and (max-width: 768px) and (min-width: 600px) {
  .supermaster-container {
    padding: 10px;
    width: auto;
  }
  
  .page-header {
    align-items: center;
  }
  
  .page-title {
    margin-bottom: 15px;
    font-size: 18px;
  }
  
  .order-steps {
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .step {
    font-size: 12px;
    padding: 5px 0;
  }
  
  .step-number {
    width: 22px;
    height: 22px;
    font-size: 12px;
    line-height: 22px;
  }
  
  .form-section {
    padding: 15px;
    margin-bottom: 15px;
  }
  
  .section-title {
    font-size: 16px;
    margin-bottom: 12px;
  }
  
  .form-row {
    /* 保持水平布局 */
    display: flex;
    flex-wrap: wrap;
  }
  
  .form-item {
    /* 每个表单项占一半宽度 */
    flex: 0 0 calc(50% - 8px);
    min-width: 200px;
    margin-bottom: 10px;
  }
  
  .form-item.full-width {
    flex: 0 0 100%;
  }
  
  .form-item label {
    width: 80px;
    text-align: right;
    margin-bottom: 0;
  }
  
  .form-input {
    flex: 1;
  }
  
  .form-actions {
    display: flex;
    justify-content: center;
    gap: 10px;
  }
  
  /* 确认页面适配 */
  .preview-section {
    padding: 12px;
  }
  
  .preview-title {
    font-size: 14px;
  }
  
  .preview-row {
    /* 保持水平布局 */
    display: flex;
    flex-wrap: wrap;
  }
  
  .preview-item {
    flex: 0 0 calc(50% - 8px);
    min-width: 200px;
    margin-bottom: 8px;
  }
  
  .preview-item.full-width {
    flex: 0 0 100%;
  }
  
  .confirmation-message {
    text-align: center;
    font-size: 13px;
    margin-bottom: 15px;
  }
  
  /* 成功页面适配 */
  .success-section {
    padding: 20px 15px;
  }
  
  .success-icon {
    font-size: 50px;
  }
  
  .success-message {
    font-size: 14px;
    margin-bottom: 20px;
  }
}

/* 手机端适配 */
@media screen and (max-width: 600px) {
  .supermaster-container {
    padding: 8px;
  }

  .page-header {
    flex-direction: column;
    padding-bottom: 10px;
    margin-bottom: 12px;
  }

  .page-title {
    font-size: 16px;
    margin-bottom: 10px;
    text-align: center;
  }

  .order-steps {
    width: 100%;
    flex-wrap: wrap;
    justify-content: center;
  }

  .step {
    font-size: 10px;
    padding: 3px 0;
  }

  .step-number {
    width: 18px;
    height: 18px;
    font-size: 10px;
    margin-left: 4px;
  }


  .form-section {
    padding: 12px;
    margin-bottom: 10px;
  }

  .form-section.beta-history-section {
    padding: 16px;
  }

  .section-title {
    font-size: 15px;
    padding-bottom: 8px;
    margin-bottom: 10px;
  }

  .form-row {
    flex-direction: column;
    gap: 6px;
    margin-bottom: 6px;
  }

  .form-item {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 8px;
  }

  .form-item label {
    width: 100%;
    text-align: left;
    padding-bottom: 4px;
    font-size: 12px;
    margin-bottom: 2px;
  }

  /* 表单控件优化 */
  :deep(.el-input__inner),
  :deep(.el-textarea__inner) {
    font-size: 13px;
  }

  :deep(.el-input-number) {
    width: 100%;
  }

  :deep(.el-date-editor.el-input),
  :deep(.el-select) {
    width: 100%;
  }

  /* 预览页面进一步优化 */
  .preview-section {
    padding: 10px;
    margin-bottom: 10px;
  }

  .preview-title {
    font-size: 14px;
    margin-bottom: 8px;
    padding-bottom: 6px;
  }

  .preview-item {
    flex-direction: column;
    margin-bottom: 6px;
  }

  .preview-label {
    width: 100%;
    font-size: 12px;
    margin-bottom: 2px;
  }

  .preview-value {
    font-size: 13px;
  }

  .confirmation-message {
    font-size: 12px;
    margin-bottom: 12px;
  }

  /* 按钮样式优化 */
  .form-actions {
    padding: 12px;
    gap: 8px;
  }

  .form-actions .el-button {
    font-size: 13px;
    padding: 8px 15px;
    height: 36px;
  }

  /* 成功页面样式优化 */
  .success-section {
    padding: 20px 10px;
  }

  .success-icon {
    font-size: 40px;
    margin-bottom: 15px;
  }

  .success-message {
    font-size: 13px;
    margin-bottom: 20px;
  }

  /* 优化右眼左眼的显示 */
  .eye-title {
    font-size: 14px;
    text-align: center;
  }
  
  .eye-data-container {
    flex-direction: column;
    gap: 10px;
  }
  
  .eye-section {
    padding: 10px;
  }
  
  /* 优化预览页面的眼睛数据显示 */
  .preview-eye-container {
    flex-direction: column;
    gap: 10px;
  }
  
  .preview-eye-title {
    text-align: center;
    font-size: 13px;
  }
  
  /* 两列布局改为单列 */
  .preview-two-columns,
  .two-columns {
    flex-direction: column;
    gap: 10px;
  }
  
  .column,
  .preview-column {
    min-width: 100%;
  }

  /* 调整单选按钮组在手机上的显示 */
  .frame-radio-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

/* 超小屏幕适配 */
@media screen and (max-width: 320px) {
  .supermaster-container {
    padding: 5px;
  }

  .page-title {
    font-size: 15px;
  }

  .step {
    font-size: 9px;
  }

  .step-number {
    width: 16px;
    height: 16px;
    font-size: 9px;
  }

  .section-title {
    font-size: 14px;
  }

  .form-item label {
    font-size: 11px;
  }

  :deep(.el-input__inner),
  :deep(.el-textarea__inner) {
    font-size: 12px;
    padding: 0 8px;
  }

  .form-actions .el-button {
    font-size: 12px;
    padding: 6px 12px;
  }

  .preview-label,
  .preview-value {
    font-size: 11px;
  }
}

/* 必填项星号样式 */
.required-star {
  color: red;
}


/* ===== 自动补全下拉项过长截断 ===== */
:global(.el-autocomplete-suggestion__wrap li) {
  max-width: 90vw;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 隐藏备注提示样式 */
.hidden-remarks-hint {
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 4px;
  color: #d46b08;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.hidden-remarks-hint i {
  font-size: 14px;
}

/* 验配单位下拉无匹配数据时的提示 */
.company-empty-tip {
  padding: 8px 12px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}
.company-empty-link {
  color: #409eff;
  text-decoration: none;
  margin: 0 2px;
}
.company-empty-link:hover {
  text-decoration: underline;
}
</style>

<style>
/* 验配单位下拉无匹配数据提示（下拉被 teleport 到 body 时生效） */
.el-select-dropdown .company-empty-tip {
  padding: 8px 12px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}
.el-select-dropdown .company-empty-link {
  color: #409eff;
  text-decoration: none;
  margin: 0 2px;
}
.el-select-dropdown .company-empty-link:hover {
  text-decoration: underline;
}
</style>
